import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { ReactRenderer } from '@atlaskit/renderer';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { xcss, Flex } from '@atlaskit/primitives';
import {
	useActions,
	useContent,
	useHasScrolledLayoutContainer,
	useHasTriggeredProactive,
	useIsGenerating,
	useIsProactivePanelShown,
} from '@atlassian/jira-issue-smart-request-summary-state/src/controllers/state/index.tsx';
import { encodeAdf } from '@atlassian/conversation-assistant-content-renderer';
import {
	onLinkClick,
	smartLinksDefault,
} from '@atlassian/jira-linking-platform-utils/src/index.tsx';
import { useProfileCardProvider } from '@atlassian/jira-profilecard-provider/src/main.tsx';
import AIContainer from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-container/index.tsx';
import { useSelector } from '@atlassian/jira-react-redux/src/index.tsx';
import { getActivityFeedInViewPort } from '@atlassian/jira-issue-view-store/src/selectors/activity-feed-selector.tsx';
import { totalCommentsSelector } from '@atlassian/jira-issue-view-store/src/selectors/comment-selector.tsx';
import { useIssueId } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useSmartSummarizeCommentsAnalytics } from '@atlassian/jira-issue-smart-request-summary-state/src/common/analytics/index.tsx';
import { AIContentBlur } from '@atlassian/ai-components';
import { FooterStreaming } from './footer/index.tsx';
import { Header } from './header/index.tsx';
import { Container, ContentWrapper } from './styled.tsx';
import { GenerationSkeleton } from './footer/skeleton/index.tsx';

const PROACTIVE_COMMENT_THRESHOLD = 5;
const LOADING_TICK = 500;

export const useIsFakeLoading = () => useState(false);

export const ProactiveSummary = () => {
	const [content] = useContent();
	const [isGenerating] = useIsGenerating();
	const cloudId = useCloudId();
	const [hasTriggeredProactive] = useHasTriggeredProactive();
	const isActivityFeedInViewPort = useSelector(getActivityFeedInViewPort);
	const totalComments = useSelector(totalCommentsSelector);
	const [hasScrolled] = useHasScrolledLayoutContainer();
	const issueId = useIssueId();
	const [isMinimizedSummary, setIsMinimizedSummary] = useState(true);
	const [hasFired, setHasFired] = useState(false);
	const [isFakeLoading, setIsFakeLoading] = useIsFakeLoading();
	const [hasTriggeredInitialPanel, setHasTriggeredInitialPanel] = useState(false);
	const dataProviders = new ProviderFactory();
	const {
		fetchProactiveSummary,
		setTriggeredProactive,
		setProactivePanelShown,
		updateProactiveSummaryCache,
	} = useActions();
	const [isProactivePanelShown] = useIsProactivePanelShown();
	const { fireTrack } = useSmartSummarizeCommentsAnalytics();
	const profileCardProvider = useProfileCardProvider();
	dataProviders.setProvider('profilecardProvider', profileCardProvider);

	const adfNode = useMemo(() => {
		if (!content) {
			return null;
		}
		return encodeAdf(content.summary, true);
	}, [content]);

	const onShowMore = useCallback(() => {
		setIsMinimizedSummary(false);
		fireTrack('aiResult viewed', 'jiraSmartSummarizeComments', {
			doesNotMeetMAUCriteria: true,
		});
		updateProactiveSummaryCache(issueId, cloudId);
	}, [fireTrack, updateProactiveSummaryCache, issueId, cloudId]);

	const onComplete = useCallback(() => {
		if (!hasFired) {
			fireTrack('aiInteraction initiated', 'jiraSmartSummarizeComments');
			setHasFired(true);
		}
	}, [fireTrack, hasFired]);

	const renderContent = () => {
		if (!adfNode) {
			return null;
		}

		return (
			<ReactRenderer
				onComplete={onComplete}
				eventHandlers={{
					link: { onClick: onLinkClick },
				}}
				document={adfNode}
				dataProviders={dataProviders}
				smartLinks={smartLinksDefault}
			/>
		);
	};

	const onClosePanel = useCallback(() => {
		setProactivePanelShown(false);
		updateProactiveSummaryCache(issueId, cloudId);
	}, [setProactivePanelShown, issueId, cloudId, updateProactiveSummaryCache]);

	useEffect(() => {
		// on mount the comment is below the threshold
		// never trigger proactive case ever, even if they
		// add more comments afterwards.
		if (totalComments < PROACTIVE_COMMENT_THRESHOLD) {
			setTriggeredProactive(true);
		} else if (!hasTriggeredProactive && content === null && issueId !== undefined) {
			fetchProactiveSummary(issueId, cloudId, fireTrack);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		/**
		 * even though we fetch the endpoint on mount for quicker experience
		 * we do not render the component until the comment section is in the viewport
		 * and the user has scrolled. Also make sure that we have actual valid
		 * content and not errors or doesn't meet our pre-conditions
		 */
		if (isActivityFeedInViewPort && hasScrolled && content !== null && !hasTriggeredInitialPanel) {
			setProactivePanelShown(true);
			setHasTriggeredInitialPanel(true);
			setIsFakeLoading(true);
		}
	}, [
		isActivityFeedInViewPort,
		hasScrolled,
		content,
		setProactivePanelShown,
		hasTriggeredInitialPanel,
		setIsFakeLoading,
	]);

	useEffect(() => {
		// we have fake loading to make the experience look smoother.
		// This is not real loading as we already have the full content available.
		if (isFakeLoading) {
			const timer = setTimeout(() => {
				setIsFakeLoading(false);
			}, LOADING_TICK);

			return () => {
				clearTimeout(timer);
			};
		}
	}, [isFakeLoading, setIsFakeLoading]);

	// generating content, or our proactive panel is not shown or
	// user has already pressed it themselves, we will enver show proactive
	if (isGenerating || !isProactivePanelShown) {
		return null;
	}

	const renderSummaryComponent = () => {
		return (
			<>
				<ContentWrapper hasContent>
					<AIContentBlur
						shouldOpenInCollapsedView={isMinimizedSummary}
						onShowMore={onShowMore}
						summaryLineHeight={80}
					>
						{renderContent()}
					</AIContentBlur>
				</ContentWrapper>
				<FooterStreaming />
			</>
		);
	};

	return (
		<Container>
			<AIContainer
				isLoading={isFakeLoading}
				shouldFitContainer
				spacing="none"
				testId="issue-smart-request-summary.ui.ai-container"
				hasNewBorderExperience
			>
				<Header onClose={onClosePanel} />
				{isFakeLoading ? (
					<Flex xcss={containerStyle}>
						<GenerationSkeleton />
					</Flex>
				) : (
					renderSummaryComponent()
				)}
			</AIContainer>
		</Container>
	);
};

const containerStyle = xcss({
	marginBlockStart: 'space.150',
});
